import { encodeSecp256k1Pubkey, makeSignDoc as makeSignDocAmino, StdFee } from '@cosmjs/amino';
import { encodeEthSecp256k1Pubkey } from '@cosmjs/amino/build/encoding';
import { isEthSecp256k1Pubkey } from '@cosmjs/amino/build/pubkeys';
import { Secp256k1 } from '@cosmjs/crypto';
import { fromBase64 } from '@cosmjs/encoding';
import { Int53, Uint53 } from '@cosmjs/math';
import {
  EncodeObject,
  encodePubkey,
  GeneratedType,
  isOfflineDirectSigner,
  makeAuthInfoBytes,
  makeSignDoc,
  OfflineSigner,
  Registry,
  TxBodyEncodeObject,
} from '@cosmjs/proto-signing';
import { isOfflineEIP712Signer } from '@cosmjs/proto-signing';
import { HttpEndpoint, Tendermint34Client, TendermintClient } from '@cosmjs/tendermint-rpc';
import { assert, assertDefined } from '@cosmjs/utils';
import { createMsgGenericAuthorization } from '@evmos/eip712';
import { GenericAuthorization, Grant } from 'cosmjs-types/cosmos/authz/v1beta1/authz';
import { Coin } from 'cosmjs-types/cosmos/base/v1beta1/coin';
import { MsgWithdrawDelegatorReward } from 'cosmjs-types/cosmos/distribution/v1beta1/tx';
import { BasicAllowance } from 'cosmjs-types/cosmos/feegrant/v1beta1/feegrant';
import { PublicKey } from 'cosmjs-types/tendermint/crypto/keys';
import { MsgDelegate, MsgUndelegate } from 'cosmjs-types/cosmos/staking/v1beta1/tx';
import { SignMode } from 'cosmjs-types/cosmos/tx/signing/v1beta1/signing';
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx';
import { MsgTransfer } from 'cosmjs-types/ibc/applications/transfer/v1/tx';
import { Height } from 'cosmjs-types/ibc/core/client/v1/client';
import Long from 'long';
import { AminoConverters, AminoTypes } from './aminotypes';
import { calculateFee, GasPrice } from './fee';
import {
  authzTypes,
  bankTypes,
  distributionTypes,
  ammTypes,
  incentivesTypes,
  feegrantTypes,
  govTypes,
  govV1Types,
  ibcTypes,
  lockupTypes,
  MsgDelegateEncodeObject,
  MsgSendEncodeObject,
  MsgTransferEncodeObject,
  MsgUndelegateEncodeObject,
  MsgWithdrawDelegatorRewardEncodeObject,
  stakingTypes,
  vestingTypes,
  erc20Types,
  rollappTypes,
  sponsorshipTypes,
  iroTypes,
  isMsgTransferEncodeObject,
  isMsgDelegateEncodeObject,
  isMsgUndelegateEncodeObject,
  isMsgBeginRedelegateEncodeObject,
  isMsgWithdrawDelegatorRewardEncodeObject,
  createTxMsgWithdrawDelegatorReward,
  isMsgSendEncodeObject,
  isMsgLockTokensEncodeObject,
  createTxMsgLockTokens,
  isMsgBeginUnlockingAllEncodeObject,
  createTxMsgBeginUnlockingAll,
  isMsgBeginUnlockingEncodeObject,
  createTxMsgBeginUnlocking,
  isMsgSwapExactAmountInEncodeObject,
  createTxMsgSwapExactAmountIn,
  isMsgJoinPoolEncodeObject,
  createTxMsgJoinPool,
  isMsgJoinSwapExternAmountInEncodeObject,
  createTxMsgJoinSwapExternAmountIn,
  isMsgExitPoolEncodeObject,
  createTxMsgExitPool,
  createTxMsgCreateBalancerPool,
  isMsgCreateBalancerPoolEncodeObject,
  isMsgVoteEncodeObject,
  isMsgDepositEncodeObject,
  createTxMsgDeposit,
  createTxMsgSubmitProposal,
  isMsgSubmitProposalEncodeObject,
  createTxMsgCreateGauge,
  isMsgCreateGaugeEncodeObject,
  isMsgCancelUnbondingDelegationEncodeObject,
  createTxMsgCancelUnbondingDelegation,
  createAuthzAminoConverters,
  createBankAminoConverters,
  createDistributionAminoConverters,
  createFeegrantAminoConverters,
  createGovAminoConverters,
  createIbcAminoConverters,
  createStakingAminoConverters,
  createVestingAminoConverters,
  isMsgCreateRollappEncodeObject,
  isMsgUpdateRollappEncodeObject,
  isMsgAddAppEncodeObject,
  isMsgUpdateAppEncodeObject,
  isMsgRemoveAppEncodeObject,
  createTxMsgCreateRollapp,
  createTxMsgUpdateRollapp,
  createTxMsgAddApp,
  createTxMsgUpdateApp,
  createTxMsgRemoveApp,
  isMsgCreatePlanEncodeObject,
  isMsgBuyEncodeObject,
  isMsgBuyExactSpendEncodeObject,
  isMsgSellEncodeObject,
  isMsgClaimEncodeObject,
  createTxMsgCreatePlan,
  createTxMsgBuy,
  createTxMsgBuyExactSpend,
  createTxMsgSell,
  createTxMsgClaim,
  delayedackTypes,
  isMsgFinalizePacketEncodeObject,
  createTxMsgFinalizePacket,
  eibcTypes,
  isMsgUpdateDemandOrderEncodeObject,
  createTxMsgUpdateDemandOrder,
  isMsgFulfillOrderEncodeObject,
  createTxMsgFulfillOrder,
  isMsgSponsorshipVoteEncodeObject,
  isMsgRevokeSponsorshipVoteEncodeObject,
  createTxMsgSponsorshipVote,
  createTxMsgSponsorshipRevokeVote,
  createTxMsgGrant,
  createTxMsgRevokeGenericGrant,
  isMsgGrantEncodeObject,
  MsgGrantEncodeObject,
  MsgGrantAllowanceEncodeObject,
  MsgRevokeAllowanceEncodeObject,
  isMsgGrantAllowanceEncodeObject,
  isMsgRevokeAllowanceEncodeObject,
  isMsgEditValidatorEncodeObject,
  isMsgRevokeEncodeObject,
  MsgRevokeEncodeObject,
  createTxMsgGrantAllowance,
  createTxMsgRevokeAllowance,
  createTxMsgCreateValidator,
  createTxMsgEditValidator,
  ethermintTypes,
  isMsgCreateValidatorEncodeObject,
} from './modules';
import { DeliverTxResponse, StargateClient, StargateClientOptions } from './stargateclient';
import { toHex, fromBech32, fromHex, toBech32 } from '@cosmjs/encoding';
import {
  Chain,
  createTxIBCMsgTransfer,
  createTxMsgBeginRedelegate,
  createTxMsgDelegate,
  createTxMsgSend,
  createTxMsgUndelegate,
  createTxMsgVote,
  Fee,
  Sender,
  TxPayload,
} from '@evmos/transactions';
import { toBase64 } from '@cosmjs/encoding';
import { createTxRaw } from '@evmos/proto';
import { Rollapp_VMType } from './modules/rollapp/ts-client/rollapp';
import { RollappPacket_Type } from './modules/delayedack/ts-client/tx';
import { MsgGrantProps } from './modules/authz/eip712/grant';

export const defaultRegistryTypes: ReadonlyArray<[ string, GeneratedType ]> = [
  [ '/cosmos.base.v1beta1.Coin', Coin ],
  ...authzTypes,
  ...bankTypes,
  ...distributionTypes,
  ...ammTypes,
  ...incentivesTypes,
  ...feegrantTypes,
  ...govTypes,
  ...govV1Types,
  ...stakingTypes,
  ...ibcTypes,
  ...lockupTypes,
  ...vestingTypes,
  ...erc20Types,
  ...rollappTypes,
  ...sponsorshipTypes,
  ...iroTypes,
  ...delayedackTypes,
  ...eibcTypes,
  ...ethermintTypes,
];

/**
 * Signing information for a single signer that is not included in the transaction.
 *
 * @see https://github.com/cosmos/cosmos-sdk/blob/v0.42.2/x/auth/signing/sign_mode_handler.go#L23-L37
 */
export interface SignerData {
  readonly accountNumber: number;
  readonly sequence: number;
  readonly chainId: string;
}

/** Use for testing only */
export interface PrivateSigningStargateClient {
  readonly registry: Registry;
}

export interface SigningStargateClientOptions extends StargateClientOptions {
  readonly registry?: Registry;
  readonly aminoTypes?: AminoTypes;
  readonly broadcastTimeoutMs?: number;
  readonly broadcastPollIntervalMs?: number;
  readonly gasPrice?: GasPrice;
  readonly evm?: boolean;
}

export function createDefaultAminoConverters(): AminoConverters {
  return {
    ...createAuthzAminoConverters(),
    ...createBankAminoConverters(),
    ...createDistributionAminoConverters(),
    ...createGovAminoConverters(),
    ...createStakingAminoConverters(),
    ...createIbcAminoConverters(),
    ...createFeegrantAminoConverters(),
    ...createVestingAminoConverters(),
  };
}

export class SigningStargateClient extends StargateClient {
  public readonly registry: Registry;
  public readonly broadcastTimeoutMs: number | undefined;
  public readonly broadcastPollIntervalMs: number | undefined;

  private readonly signer: OfflineSigner;
  private readonly aminoTypes: AminoTypes;
  private readonly gasPrice: GasPrice | undefined;
  private readonly evm?: boolean;

  /**
   * Creates an instance by connecting to the given Tendermint RPC endpoint.
   *
   * For now this uses the Tendermint 0.34 client. If you need Tendermint 0.37
   * support, see `createWithSigner`.
   */
  public static async connectWithSigner(
    endpoint: string | HttpEndpoint,
    signer: OfflineSigner,
    options: SigningStargateClientOptions = {},
  ): Promise<SigningStargateClient> {
    const tmClient = await Tendermint34Client.connect(endpoint);
    return SigningStargateClient.createWithSigner(tmClient, signer, options);
  }

  /**
   * Creates an instance from a manually created Tendermint client.
   * Use this to use `Tendermint37Client` instead of `Tendermint34Client`.
   */
  public static async createWithSigner(
    tmClient: TendermintClient,
    signer: OfflineSigner,
    options: SigningStargateClientOptions = {},
  ): Promise<SigningStargateClient> {
    return new SigningStargateClient(tmClient, signer, options);
  }

  /**
   * Creates a client in offline mode.
   *
   * This should only be used in niche cases where you know exactly what you're doing,
   * e.g. when building an offline signing application.
   *
   * When you try to use online functionality with such a signer, an
   * exception will be raised.
   */
  public static async offline(
    signer: OfflineSigner,
    options: SigningStargateClientOptions = {},
  ): Promise<SigningStargateClient> {
    return new SigningStargateClient(undefined, signer, options);
  }

  protected constructor(
    tmClient: TendermintClient | undefined,
    signer: OfflineSigner,
    options: SigningStargateClientOptions,
  ) {
    super(tmClient, options);
    const {
      registry = new Registry(defaultRegistryTypes),
      aminoTypes = new AminoTypes(createDefaultAminoConverters()),
    } = options;
    this.registry = registry;
    this.aminoTypes = aminoTypes;
    this.signer = signer;
    this.broadcastTimeoutMs = options.broadcastTimeoutMs;
    this.broadcastPollIntervalMs = options.broadcastPollIntervalMs;
    this.gasPrice = options.gasPrice;
    this.evm = options.evm;
  }

  public async simulate(
    signerAddress: string,
    messages: readonly EncodeObject[],
    memo: string | undefined,
  ): Promise<number> {
    const anyMsgs = messages.map((m) => this.registry.encodeAsAny(m));
    const accountFromSigner = (await this.signer.getAccounts()).find(
      (account) => account.address === signerAddress,
    );
    if (!accountFromSigner) {
      throw new Error('Failed to retrieve account from signer');
    }
    const pubkey = this.evm ?
      encodeEthSecp256k1Pubkey(accountFromSigner.pubkey) :
      encodeSecp256k1Pubkey(accountFromSigner.pubkey);
    const { sequence } = await this.getSequence(signerAddress);
    const { gasInfo } = await this.forceGetQueryClient().tx.simulate(anyMsgs, memo, pubkey, sequence);
    assertDefined(gasInfo);
    return Uint53.fromString(gasInfo.gasUsed.toString()).toNumber();
  }

  public async sendTokens(
    senderAddress: string,
    recipientAddress: string,
    amount: readonly Coin[],
    fee: StdFee | 'auto' | number,
    memo = '',
  ): Promise<DeliverTxResponse> {
    const sendMsg: MsgSendEncodeObject = {
      typeUrl: '/cosmos.bank.v1beta1.MsgSend',
      value: {
        fromAddress: senderAddress,
        toAddress: recipientAddress,
        amount: [ ...amount ],
      },
    };
    return this.signAndBroadcast(senderAddress, [ sendMsg ], fee, memo);
  }

  public async delegateTokens(
    delegatorAddress: string,
    validatorAddress: string,
    amount: Coin,
    fee: StdFee | 'auto' | number,
    memo = '',
  ): Promise<DeliverTxResponse> {
    const delegateMsg: MsgDelegateEncodeObject = {
      typeUrl: '/cosmos.staking.v1beta1.MsgDelegate',
      value: MsgDelegate.fromPartial({
        delegatorAddress: delegatorAddress,
        validatorAddress: validatorAddress,
        amount: amount,
      }),
    };
    return this.signAndBroadcast(delegatorAddress, [ delegateMsg ], fee, memo);
  }

  public async undelegateTokens(
    delegatorAddress: string,
    validatorAddress: string,
    amount: Coin,
    fee: StdFee | 'auto' | number,
    memo = '',
  ): Promise<DeliverTxResponse> {
    const undelegateMsg: MsgUndelegateEncodeObject = {
      typeUrl: '/cosmos.staking.v1beta1.MsgUndelegate',
      value: MsgUndelegate.fromPartial({
        delegatorAddress: delegatorAddress,
        validatorAddress: validatorAddress,
        amount: amount,
      }),
    };
    return this.signAndBroadcast(delegatorAddress, [ undelegateMsg ], fee, memo);
  }

  public async withdrawRewards(
    delegatorAddress: string,
    validatorAddress: string,
    fee: StdFee | 'auto' | number,
    memo = '',
  ): Promise<DeliverTxResponse> {
    const withdrawMsg: MsgWithdrawDelegatorRewardEncodeObject = {
      typeUrl: '/cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward',
      value: MsgWithdrawDelegatorReward.fromPartial({
        delegatorAddress: delegatorAddress,
        validatorAddress: validatorAddress,
      }),
    };
    return this.signAndBroadcast(delegatorAddress, [ withdrawMsg ], fee, memo);
  }

  public async sendIbcTokens(
    senderAddress: string,
    recipientAddress: string,
    transferAmount: Coin,
    sourcePort: string,
    sourceChannel: string,
    timeoutHeight: Height | undefined,
    /** timeout in seconds */
    timeoutTimestamp: number | undefined,
    fee: StdFee | 'auto' | number,
    memo = '',
  ): Promise<DeliverTxResponse> {
    const timeoutTimestampNanoseconds = timeoutTimestamp
      ? Long.fromNumber(timeoutTimestamp).multiply(1_000_000_000)
      : undefined;
    const transferMsg: MsgTransferEncodeObject = {
      typeUrl: '/ibc.applications.transfer.v1.MsgTransfer',
      value: MsgTransfer.fromPartial({
        sourcePort: sourcePort,
        sourceChannel: sourceChannel,
        sender: senderAddress,
        receiver: recipientAddress,
        token: transferAmount,
        timeoutHeight: timeoutHeight,
        timeoutTimestamp: timeoutTimestampNanoseconds,
      }),
    };
    return this.signAndBroadcast(senderAddress, [ transferMsg ], fee, memo);
  }

  public async signAndBroadcast(
    signerAddress: string,
    messages: readonly EncodeObject[],
    fee: StdFee | 'auto' | number,
    memo = '',
    signMethod?: 'eip712' | 'direct' | 'amino',
  ): Promise<DeliverTxResponse> {
    let usedFee: StdFee;
    if (fee == 'auto' || typeof fee === 'number') {
      assertDefined(this.gasPrice, 'Gas price must be set in the client options when auto gas is used.');
      const gasEstimation = await this.simulate(signerAddress, messages, memo);
      const multiplier = typeof fee === 'number' ? fee : 1.3;
      usedFee = calculateFee(Math.round(gasEstimation * multiplier), this.gasPrice);
    } else {
      usedFee = fee;
    }
    const txRaw = await this.sign(signerAddress, messages, usedFee, memo, undefined, signMethod);
    const txBytes = TxRaw.encode(txRaw).finish();
    return this.broadcastTx(txBytes, this.broadcastTimeoutMs, this.broadcastPollIntervalMs);
  }

  /**
   * Gets account number and sequence from the API, creates a sign doc,
   * creates a single signature and assembles the signed transaction.
   *
   * The sign mode (SIGN_MODE_DIRECT or SIGN_MODE_LEGACY_AMINO_JSON) is determined by this client's signer.
   *
   * You can pass signer data (account number, sequence and chain ID) explicitly instead of querying them
   * from the chain. This is needed when signing for a multisig account, but it also allows for offline signing
   * (See the SigningStargateClient.offline constructor).
   */
  public async sign(
    signerAddress: string,
    messages: readonly EncodeObject[],
    fee: StdFee,
    memo: string,
    explicitSignerData?: SignerData,
    signMethod?: 'eip712' | 'direct' | 'amino',
  ): Promise<TxRaw> {
    let signerData: SignerData;
    if (explicitSignerData) {
      signerData = explicitSignerData;
    } else {
      const { accountNumber, sequence } = await this.getSequence(signerAddress);
      const chainId = await this.getChainId();
      signerData = {
        accountNumber: accountNumber,
        sequence: sequence,
        chainId: chainId,
      };
    }

    if ((!signMethod || signMethod === 'eip712') && isOfflineEIP712Signer(this.signer)) {
      return this.signEIP712(signerAddress, messages, fee, memo, signerData);
    } else if ((!signMethod || signMethod === 'direct') && isOfflineDirectSigner(this.signer)) {
      return this.signDirect(signerAddress, messages, fee, memo, signerData);
    } else {
      return this.signAmino(signerAddress, messages, fee, memo, signerData);
    }
  }

  private async signAmino(
    signerAddress: string,
    messages: readonly EncodeObject[],
    fee: StdFee,
    memo: string,
    { accountNumber, sequence, chainId }: SignerData,
  ): Promise<TxRaw> {
    assert(!isOfflineDirectSigner(this.signer) && !isOfflineEIP712Signer(this.signer));
    const accountFromSigner = (await this.signer.getAccounts()).find(
      (account) => account.address === signerAddress,
    );
    if (!accountFromSigner) {
      throw new Error('Failed to retrieve account from signer');
    }
    const pubkey = encodePubkey(this.evm ?
      encodeEthSecp256k1Pubkey(accountFromSigner.pubkey) :
      encodeSecp256k1Pubkey(accountFromSigner.pubkey),
    );
    const signMode = SignMode.SIGN_MODE_LEGACY_AMINO_JSON;
    const msgs = messages.map((msg) => this.aminoTypes.toAmino(msg));
    const signDoc = makeSignDocAmino(msgs, fee, chainId, memo, accountNumber, sequence);
    const { signature, signed } = await this.signer.signAmino(signerAddress, signDoc);
    const signedTxBody = {
      messages: signed.msgs.map((msg) => this.aminoTypes.fromAmino(msg)),
      memo: signed.memo,
    };
    const signedTxBodyEncodeObject: TxBodyEncodeObject = {
      typeUrl: '/cosmos.tx.v1beta1.TxBody',
      value: signedTxBody,
    };
    const signedTxBodyBytes = this.registry.encode(signedTxBodyEncodeObject);
    const signedGasLimit = Int53.fromString(signed.fee.gas).toNumber();
    const signedSequence = Int53.fromString(signed.sequence).toNumber();
    const signedAuthInfoBytes = makeAuthInfoBytes(
      [ { pubkey, sequence: signedSequence } ],
      signed.fee.amount,
      signedGasLimit,
      signed.fee.granter,
      signed.fee.payer,
      signMode,
    );
    return TxRaw.fromPartial({
      bodyBytes: signedTxBodyBytes,
      authInfoBytes: signedAuthInfoBytes,
      signatures: [ fromBase64(signature.signature) ],
    });
  }

  private async signEIP712(
    signerAddress: string,
    messages: readonly EncodeObject[],
    fee: StdFee,
    memo: string,
    { accountNumber, sequence, chainId }: SignerData,
  ): Promise<TxRaw> {
    assert(isOfflineEIP712Signer(this.signer));
    const accountFromSigner = (await this.signer.getAccounts()).find((account) => account.address === signerAddress);
    if (!accountFromSigner) {
      throw new Error('Failed to retrieve account from signer');
    }
    // todo: check if can replace with pub-key
    const pubkey = encodePubkey(this.evm ?
      encodeEthSecp256k1Pubkey(accountFromSigner.pubkey) :
      encodeSecp256k1Pubkey(accountFromSigner.pubkey),
    );

    const hexSignerAddress = '0x' + toHex(fromBech32(signerAddress).data);
    const chain: Chain = { chainId: Number(chainId.split(/[_-]/)[1]), cosmosChainId: chainId };
    const sender: Sender = {
      accountAddress: signerAddress,
      sequence: sequence,
      accountNumber: accountNumber,
      pubkey: toBase64(accountFromSigner?.pubkey || new Uint8Array([])),
    };
    const eip712Fee: Fee = { ...fee.amount[0], gas: fee.gas };
    const txPayload = toEip712TransactionPayload(chain, sender, eip712Fee, [ ...messages ], memo);
    const { eipToSign, signDirect } = txPayload;
    const signature = await this.signer.signEIP712(signerAddress, eipToSign);
    const rawTx = createTxRaw(signDirect.body.toBinary(), signDirect.authInfo.toBinary(), [ signature ]);
    return rawTx.message;
  }

  private async signDirect(
    signerAddress: string,
    messages: readonly EncodeObject[],
    fee: StdFee,
    memo: string,
    { accountNumber, sequence, chainId }: SignerData,
  ): Promise<TxRaw> {
    assert(isOfflineDirectSigner(this.signer));
    const accountFromSigner = (await this.signer.getAccounts()).find(
      (account) => account.address === signerAddress,
    );
    if (!accountFromSigner) {
      throw new Error('Failed to retrieve account from signer');
    }
    const pubkey = encodePubkey(this.evm ?
      encodeEthSecp256k1Pubkey(accountFromSigner.pubkey) :
      encodeSecp256k1Pubkey(accountFromSigner.pubkey),
    );
    const txBodyEncodeObject: TxBodyEncodeObject = {
      typeUrl: '/cosmos.tx.v1beta1.TxBody',
      value: {
        messages: messages,
        memo: memo,
      },
    };
    const txBodyBytes = this.registry.encode(txBodyEncodeObject);
    const gasLimit = Int53.fromString(fee.gas).toNumber();
    const authInfoBytes = makeAuthInfoBytes(
      [ { pubkey, sequence } ],
      fee.amount,
      gasLimit,
      fee.granter,
      fee.payer,
    );
    const signDoc = makeSignDoc(txBodyBytes, authInfoBytes, chainId, accountNumber);
    const { signature, signed } = await this.signer.signDirect(signerAddress, signDoc);
    return TxRaw.fromPartial({
      bodyBytes: signed.bodyBytes,
      authInfoBytes: signed.authInfoBytes,
      signatures: [ fromBase64(signature.signature) ],
    });
  }
}

export function toEip712TransactionPayload(
  chain: Chain,
  sender: Sender,
  fee: Fee,
  messages: EncodeObject | EncodeObject[],
  memo = '',
): TxPayload {
  const message = Array.isArray(messages) ? messages[0] : messages;
  const messageList = Array.isArray(messages) ? messages : [ messages ];
  if (isMsgTransferEncodeObject(message)) {
    const timeoutInNanoseconds = message.value.timeoutTimestamp?.toString() || '';
    const timeoutInDays = timeoutInNanoseconds.substring(0, 8).padEnd(timeoutInNanoseconds.length, '0'); // todo: add day or fix this method
    return createTxIBCMsgTransfer({ chain, sender, fee, memo }, {
      amount: message.value.token?.amount || '0',
      denom: message.value.token?.denom || '',
      sourcePort: message.value.sourcePort || '',
      sourceChannel: message.value.sourceChannel || '',
      timeoutTimestamp: timeoutInDays,
      receiver: message.value.receiver || '',
      memo: message.value.memo,
      revisionHeight: (Number(message.value.timeoutHeight?.revisionHeight) || '0') as number,
      revisionNumber: (Number(message.value.timeoutHeight?.revisionNumber) || '0') as number,
    });
  } else if (isMsgDelegateEncodeObject(message)) {
    return createTxMsgDelegate({ chain, sender, fee, memo }, {
      amount: message.value.amount?.amount || '0',
      denom: message.value.amount?.denom || '',
      validatorAddress: message.value.validatorAddress || '',
    });
  } else if (isMsgUndelegateEncodeObject(message)) {
    return createTxMsgUndelegate({ chain, sender, fee, memo }, {
      amount: message.value.amount?.amount || '0',
      denom: message.value.amount?.denom || '',
      validatorAddress: message.value.validatorAddress || '',
    });
  } else if (isMsgBeginRedelegateEncodeObject(message)) {
    return createTxMsgBeginRedelegate({ chain, sender, fee, memo }, {
      amount: message.value.amount?.amount || '0',
      denom: message.value.amount?.denom || '',
      validatorSrcAddress: message.value.validatorSrcAddress || '',
      validatorDstAddress: message.value.validatorDstAddress || '',
    });
  } else if (isMsgWithdrawDelegatorRewardEncodeObject(message)) {
    return createTxMsgWithdrawDelegatorReward({ chain, sender, fee, memo }, messageList.map((message) => ({
      validatorAddress: message.value.validatorAddress || '',
    })));
  } else if (isMsgSendEncodeObject(message)) {
    return createTxMsgSend({ chain, sender, fee, memo }, {
      amount: message.value.amount?.[0]?.amount || '0',
      denom: message.value.amount?.[0]?.denom || '',
      destinationAddress: message.value.toAddress || '',
    });
  } else if (isMsgLockTokensEncodeObject(message)) {
    return createTxMsgLockTokens({ chain, sender, fee, memo }, {
      owner: message.value.owner || '',
      coins: message.value.coins || [],
      duration: message.value.duration,
    });
  } else if (isMsgBeginUnlockingAllEncodeObject(message)) {
    return createTxMsgBeginUnlockingAll({ chain, sender, fee, memo }, {
      owner: message.value.owner || '',
    });
  } else if (isMsgBeginUnlockingEncodeObject(message)) {
    return createTxMsgBeginUnlocking({ chain, sender, fee, memo }, {
      owner: message.value.owner || '',
      ID: message.value.ID || 0,
      coins: message.value.coins || [],
    });
  } else if (isMsgSwapExactAmountInEncodeObject(message)) {
    return createTxMsgSwapExactAmountIn({ chain, sender, fee, memo }, {
      sender: message.value.sender || '',
      tokenIn: message.value.tokenIn,
      tokenOutMinAmount: message.value.tokenOutMinAmount || '0',
      routes: message.value.routes || [],
    });
  } else if (isMsgJoinPoolEncodeObject(message)) {
    return createTxMsgJoinPool({ chain, sender, fee, memo }, {
      sender: message.value.sender || '',
      poolId: message.value.poolId || 0,
      shareOutAmount: message.value.shareOutAmount || '0',
      tokenInMaxs: message.value.tokenInMaxs || [],
    });
  } else if (isMsgJoinSwapExternAmountInEncodeObject(message)) {
    return createTxMsgJoinSwapExternAmountIn({ chain, sender, fee, memo }, {
      sender: message.value.sender || '',
      poolId: message.value.poolId || 0,
      tokenIn: message.value.tokenIn,
      shareOutMinAmount: message.value.shareOutMinAmount || '0',
    });
  } else if (isMsgExitPoolEncodeObject(message)) {
    return createTxMsgExitPool({ chain, sender, fee, memo }, {
      sender: message.value.sender || '',
      poolId: message.value.poolId || 0,
      shareInAmount: message.value.shareInAmount || '0',
      tokenOutMins: message.value.tokenOutMins || [],
    });
  } else if (isMsgCreateBalancerPoolEncodeObject(message)) {
    return createTxMsgCreateBalancerPool({ chain, sender, fee, memo }, {
      sender: message.value.sender || '',
      poolParams: message.value.poolParams,
      poolAssets: message.value.poolAssets || [],
      futurePoolGovernor: message.value.futurePoolGovernor || '',
    });
  } else if (isMsgVoteEncodeObject(message)) {
    return createTxMsgVote({ chain, sender, fee, memo }, {
      proposalId: Number(message.value.proposalId) || 0,
      option: Number(message.value.option),
    });
  } else if (isMsgDepositEncodeObject(message)) {
    return createTxMsgDeposit({ chain, sender, fee, memo }, {
      proposalId: Number(message.value.proposalId) || 0,
      deposit: {
        amount: message.value.amount?.[0]?.amount || '0',
        denom: message.value.amount?.[0]?.denom || '',
      },
    });
  } else if (isMsgSubmitProposalEncodeObject(message)) {
    return createTxMsgSubmitProposal({ chain, sender, fee, memo }, {
      // content: {
      //   path: message.value.content?.typeUrl.replace('/', '') || '',
      //   message: {
      //     toBinary: () => message.value.content?.value as any,
      //     toJSON: () => {
      //       const content = message.value.content && ParameterChangeProposal.decode(message.value.content.value);
      //       return {
      //         type: message.value.content?.typeUrl,
      //         value: {
      //           title: content?.title,
      //           description: content?.description,
      //           changes: content?.changes.map((item) => ({
      //             subspace: item.subspace,
      //             key: item.key,
      //             value: item.value,
      //           })),
      //         },
      //       }
      //     }
      //   } as any
      // },
      content: message.value.content,
      proposer: message.value.proposer || '',
      // denom: message.value.initialDeposit?.[0]?.denom || '',
      // amount: message.value.initialDeposit?.[0]?.amount || '0',
      initialDeposit: message.value.initialDeposit || [],
    });
  } else if (isMsgCreateGaugeEncodeObject(message)) {
    return createTxMsgCreateGauge({ chain, sender, fee, memo }, {
      isPerpetual: message.value.isPerpetual || false,
      owner: message.value.owner || '',
      distributeTo: message.value.distributeTo,
      coins: message.value.coins || [],
      startTime: message.value.startTime,
      numEpochsPaidOver: message.value.numEpochsPaidOver || 0,
    });
  } else if (isMsgCancelUnbondingDelegationEncodeObject(message)) {
    return createTxMsgCancelUnbondingDelegation({ chain, sender, fee, memo }, {
      delegatorAddress: message.value.delegatorAddress || '',
      validatorAddress: message.value.validatorAddress || '',
      amount: message.value.amount,
      creationHeight: message.value.creationHeight || 0,
    });
  } else if (isMsgCreateRollappEncodeObject(message)) {
    return createTxMsgCreateRollapp({ chain, sender, fee, memo }, {
      rollappId: message.value.rollappId || '',
      alias: message.value.alias || '',
      creator: message.value.creator || '',
      genesisInfo: message.value.genesisInfo,
      initialSequencer: message.value.initialSequencer || '',
      minSequencerBond: message.value.minSequencerBond,
      metadata: message.value.metadata,
      vmType: message.value.vmType || Rollapp_VMType.UNRECOGNIZED,
    });
  } else if (isMsgUpdateRollappEncodeObject(message)) {
    return createTxMsgUpdateRollapp({ chain, sender, fee, memo }, {
      rollappId: message.value.rollappId || '',
      genesisInfo: message.value.genesisInfo,
      metadata: message.value.metadata,
      minSequencerBond: message.value.minSequencerBond,
      owner: message.value.owner || '',
      initialSequencer: message.value.initialSequencer || '',
    });
  } else if (isMsgAddAppEncodeObject(message)) {
    return createTxMsgAddApp({ chain, sender, fee, memo }, {
      rollappId: message.value.rollappId || '',
      creator: message.value.creator || '',
      name: message.value.name || '',
      order: message.value.order || 0,
      url: message.value.url || '',
      image: message.value.image || '',
      description: message.value.description || '',
    });
  } else if (isMsgUpdateAppEncodeObject(message)) {
    return createTxMsgUpdateApp({ chain, sender, fee, memo }, {
      rollappId: message.value.rollappId || '',
      creator: message.value.creator || '',
      name: message.value.name || '',
      order: message.value.order || 0,
      url: message.value.url || '',
      image: message.value.image || '',
      description: message.value.description || '',
      id: message.value.id || 0,
    });
  } else if (isMsgRemoveAppEncodeObject(message)) {
    return createTxMsgRemoveApp({ chain, sender, fee, memo }, {
      rollappId: message.value.rollappId || '',
      creator: message.value.creator || '',
      id: message.value.id || 0,
    });
  } else if (isMsgCreatePlanEncodeObject(message)) {
    return createTxMsgCreatePlan({ chain, sender, fee, memo }, {
      owner: message.value.owner || '',
      rollappId: message.value.rollappId || '',
      startTime: message.value.startTime,
      iroPlanDuration: message.value.iroPlanDuration,
      allocatedAmount: message.value.allocatedAmount || '0',
      bondingCurve: message.value.bondingCurve,
      incentivePlanParams: message.value.incentivePlanParams,
    });
  } else if (isMsgBuyEncodeObject(message)) {
    return createTxMsgBuy({ chain, sender, fee, memo }, {
      buyer: message.value.buyer || '',
      planId: message.value.planId || '',
      amount: message.value.amount || '',
      maxCostAmount: message.value.maxCostAmount || '',
    });
  } else if (isMsgBuyExactSpendEncodeObject(message)) {
    return createTxMsgBuyExactSpend({ chain, sender, fee, memo }, {
      buyer: message.value.buyer || '',
      planId: message.value.planId || '',
      spend: message.value.spend || '',
      minOutTokensAmount: message.value.minOutTokensAmount || '',
    });
  } else if (isMsgSellEncodeObject(message)) {
    return createTxMsgSell({ chain, sender, fee, memo }, {
      seller: message.value.seller || '',
      planId: message.value.planId || '',
      amount: message.value.amount || '',
      minIncomeAmount: message.value.minIncomeAmount || '',
    });
  } else if (isMsgClaimEncodeObject(message)) {
    return createTxMsgClaim({ chain, sender, fee, memo }, {
      claimer: message.value.claimer || '',
      planId: message.value.planId || '',
    });
  } else if (isMsgFinalizePacketEncodeObject(message)) {
    return createTxMsgFinalizePacket({ chain, sender, fee, memo }, messageList.map((message) => ({
      sender: message.value.sender || '',
      rollappId: message.value.rollappId || '',
      packetProofHeight: message.value.packetProofHeight || 0,
      packetType: message.value.packetType as number,
      packetSequence: message.value.packetSequence || 0,
      packetSrcChannel: message.value.packetSrcChannel || '',
    })));
  } else if (isMsgFulfillOrderEncodeObject(message)) {
    return createTxMsgFulfillOrder({ chain, sender, fee, memo }, {
      expectedFee: message.value.expectedFee || '',
      orderId: message.value.orderId || '',
      fulfillerAddress: message.value.fulfillerAddress || '',
    });
  } else if (isMsgUpdateDemandOrderEncodeObject(message)) {
    return createTxMsgUpdateDemandOrder({ chain, sender, fee, memo }, {
      newFee: message.value.newFee || '',
      orderId: message.value.orderId || '',
      ownerAddress: message.value.ownerAddress || '',
    });
  } else if (isMsgSponsorshipVoteEncodeObject(message)) {
    return createTxMsgSponsorshipVote({ chain, sender, fee, memo }, {
      voter: message.value.voter || '',
      weights: message.value.weights || [],
    });
  } else if (isMsgRevokeSponsorshipVoteEncodeObject(message)) {
    return createTxMsgSponsorshipRevokeVote({ chain, sender, fee, memo }, {
      voter: message.value.voter || '',
    });
  } else if (isMsgGrantEncodeObject(message)) {
    return createTxMsgGrant({ chain, sender, fee, memo }, (messageList as MsgGrantEncodeObject[])
      .filter((message) => message.value.grant?.authorization)
      .map<MsgGrantProps>((message) => ({
        granter: message.value.granter || '',
        grantee: message.value.grantee || '',
        expiration: message.value.grant?.expiration,
        authorization: message.value.grant?.authorization!,
      })),
    );
  } else if (isMsgRevokeEncodeObject(message)) {
    return createTxMsgRevokeGenericGrant({ chain, sender, fee, memo }, (messageList as MsgRevokeEncodeObject[])
      .map((message) => ({ typeUrl: message.value.msgTypeUrl || '', granteeAddress: message.value.grantee || '' })));
  } else if (isMsgGrantAllowanceEncodeObject(message)) {
    const basicAllowance = message.value.allowance ?
      BasicAllowance.decode(message.value.allowance.value) : BasicAllowance.fromPartial({});
    return createTxMsgGrantAllowance(
      { chain, sender, fee, memo }, { granteeAddress: message.value.grantee || '', basicAllowance },
    );
  } else if (isMsgRevokeAllowanceEncodeObject(message)) {
    return createTxMsgRevokeAllowance({ chain, sender, fee, memo }, (messageList as MsgRevokeAllowanceEncodeObject[])
      .map((message) => ({ granteeAddress: message.value.grantee || '' })));
  } else if (isMsgCreateValidatorEncodeObject(message)) {
    if (!message.value.value || !message.value.pubkey || !message.value.description || !message.value.commission) {
      throw new Error('Invalid message: ' + JSON.stringify(message));
    }
    return createTxMsgCreateValidator({ chain, sender, fee, memo }, {
      value: message.value.value,
      pubkey: message.value.pubkey,
      validatorAddress: message.value.validatorAddress || '',
      description: message.value.description,
      delegatorAddress: message.value.delegatorAddress || '',
      commission: message.value.commission,
      minSelfDelegation: message.value.minSelfDelegation || '0',
    });
  } else if (isMsgEditValidatorEncodeObject(message)) {
    if (!message.value.validatorAddress && !message.value.description && !message.value.commissionRate) {
      throw new Error('Invalid message: ' + JSON.stringify(message));
    }
    return createTxMsgEditValidator({ chain, sender, fee, memo }, {
      validatorAddress: message.value.validatorAddress || '',
      description: message.value.description,
      commissionRate: message.value.commissionRate || '',
    });
  }
  throw new Error('Message not supported: ' + JSON.stringify(message));
}
